/**
 * One book's place on this device, for a surface that draws it.
 *
 * The shelves-and-drawers argument for this hook being a hook: the place is device
 * state, it changes while the *reader* is on screen, and the surface that draws it
 * is usually somewhere else entirely — a feed card, in a row that knows nothing
 * about when the reader came or went. So the card is subscribed to the store rather
 * than told: a learner who reads three pages and scrolls back to the feed finds the
 * card they left saying "Continue" instead of "Read the book".
 *
 * The rules — *whether* a book has something to continue, and how far in it is —
 * are in `#/lib/flashbookShelf`, which is pure. The reader's own authority is
 * borrowed there (see `flashBookPlace`), so a card and the book's cover cannot
 * disagree about whether there is a continue to offer.
 */
import {useSyncExternalStore} from 'react'

import {
  type FlashBookProgressIndex,
  flashBookProgressVersion,
  readProgressIndex,
  subscribeFlashBookProgress,
} from '#/lib/flashbookProgressStore'
import {flashBookPlace, type FlashBookShelfItem} from '#/lib/flashbookShelf'

/**
 * The device's places, as of a version of the store.
 *
 * This exists to make the version a *real* input of the hook. Asked plainly — read
 * the index, then look a book up in it — the whole thing is a function of `bookUri`
 * as far as the compiler is concerned, and it caches the first answer: a card would
 * go on saying "Read the book" to a learner who is halfway through one, because the
 * write that told the store it had changed is not something the compiler can see.
 * Handing the version to a function and reading the store inside it makes the
 * dependency legible, and a version is exactly what a cached read should be keyed on.
 *
 * The index is remembered rather than re-read per card, so a feed of thirty rows
 * costs one storage read per write rather than thirty.
 */
let cached: {version: number; index: FlashBookProgressIndex} | null = null

function indexAt(version: number): FlashBookProgressIndex {
  if (!cached || cached.version !== version) {
    cached = {version, index: readProgressIndex()}
  }
  return cached.index
}

/**
 * The place this device holds in a book, or `null` when there is nothing to
 * continue.
 *
 * `bookUri` is the *record* the reader reads — a post's back-ref, or the record a
 * lookup found — because that is what a read is keyed by: the same book reached
 * from a feed, a message or the shelf is one read, and a card that asked about the
 * post would find nothing. It may arrive late (the record a feed row has to look up
 * is not there on the first paint), so the lookup simply runs again when it does.
 *
 * What is subscribed to is the store's *version*, not its contents: a parsed index is
 * a new object on every read, so a subscription to that would re-render for ever. The
 * version changes when a place is written; what changed is read from storage, which
 * is the only copy of the truth. Before there is a device to ask — the web build's
 * first render — the version is the same zero, which draws the card as a device that
 * has never read the book: the honest thing to show.
 */
export function useFlashBookPlace(
  bookUri: string | null | undefined,
): FlashBookShelfItem | null {
  const version = useSyncExternalStore(
    subscribeFlashBookProgress,
    flashBookProgressVersion,
    () => 0,
  )

  const index = indexAt(version)
  return bookUri ? flashBookPlace(index, bookUri) : null
}
